import * as CL from '@design-system/component-library';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses.js';
import { checkAvailabilityMsg, somethingWentWrongMsg, t } from '../../../../common/i18n/index.js';
import { DatePicker } from '../../../../common/react-hook-form/components/DatePicker.js';
import { InstallationAvailabilityStatus } from '../../../../common/types/installationDatesCoCo.js';
import {
  getAvailableDates,
  getInstallationTimeSlotIdsForDate,
  normalizeDatePickerValue,
} from '../../../../common/utils/dateUtilsCoCo.js';
import type { PlaceOrderFormDataCoCo } from '../../placeOrderTypesCoCo.js';
import type { InstallationDateSelectionProps } from '../productConfigTypesCoCo.js';

import './InstallationDateSelectionCoCo.scss';

export type InstallationDateSelectionCoCoProps = InstallationDateSelectionProps;

export const InstallationDateSelectionCoCo = ({
  availableInstallationDates,
  installationAvailability,
  validationMessage,
  showExistingSubscriptionTerminationNotice = true,
  onCheckAvailability,
  onConfirmInstallationTimeSlots,
}: InstallationDateSelectionCoCoProps) => {
  const { setValue, watch } = useFormContext<PlaceOrderFormDataCoCo>();
  const selectedDate = watch('products.preferredInstallationDate') || '';
  const normalizedSelectedDate = normalizeDatePickerValue(selectedDate);
  const [confirmedDate, setConfirmedDate] = useState<string>();
  const [isConfirming, setIsConfirming] = useState(false);
  const availabilityResults = installationAvailability?.results ?? availableInstallationDates;
  const availabilityStatus =
    installationAvailability?.status ??
    (availabilityResults.length > 0 ? InstallationAvailabilityStatus.SUCCESS : InstallationAvailabilityStatus.IDLE);
  const hasCurrentAvailability = availabilityStatus === InstallationAvailabilityStatus.SUCCESS;
  const availableDates = hasCurrentAvailability ? getAvailableDates(availabilityResults) : [];
  const hasSelectedDate = availableDates.includes(normalizedSelectedDate);
  const selectedTimeSlotIds = getInstallationTimeSlotIdsForDate(availabilityResults, normalizedSelectedDate);
  const canConfirmDate =
    hasCurrentAvailability &&
    availabilityResults.length > 0 &&
    availabilityResults.every(result => Boolean(selectedTimeSlotIds[result.cartItemId]));
  const hasUnavailableSelectedDate = Boolean(selectedDate) && !hasSelectedDate && availableDates.length > 0;
  const isCheckingAvailability = availabilityStatus === InstallationAvailabilityStatus.LOADING;

  useEffect(() => {
    if (availabilityStatus !== InstallationAvailabilityStatus.SUCCESS) {
      setConfirmedDate(undefined);
    }
  }, [availabilityStatus]);

  const selectDate = (date: string) => {
    setValue('products.preferredInstallationDate', date, { shouldDirty: true, shouldValidate: true });
    setConfirmedDate(undefined);
  };

  const checkAvailability = () => {
    setConfirmedDate(undefined);
    return onCheckAvailability?.();
  };

  const confirmDate = async () => {
    setIsConfirming(true);
    try {
      const confirmed = (await onConfirmInstallationTimeSlots?.(selectedTimeSlotIds)) ?? false;
      if (confirmed) {
        setValue('products.installationAppointmentTimeSlotIds', selectedTimeSlotIds, { shouldDirty: true });
        setConfirmedDate(normalizedSelectedDate);
      }
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <section className="of-installation-date-selection">
      <h2 className="of-installation-date-selection__heading">
        {t.RF14('Preferred installation date of subscription')}
      </h2>
      {showExistingSubscriptionTerminationNotice && (
        <CL.Notification
          hasCloseButton={false}
          text={t.FEPO('In case there is existing subscription, it needs to be terminated before the delivery date.')}
          type="info"
          visible
        />
      )}
      <p>
        {t.D55A(
          'If installation date is left empty then the broadband will be delivered as soon as possible. If the subscription replaces an existing subscription, enter the termination date of the existing subscription here.'
        )}
      </p>
      {validationMessage && (
        <p className="of-installation-date-selection__error" role="alert">
          {validationMessage}
        </p>
      )}
      <div className={`of-installation-date-selection__date-picker ${dsClass.MARGIN_TOP_3}`}>
        <DatePicker
          name="products.preferredInstallationDate"
          label={t.HFZQ('Pick a preferred installation date')}
          disableAllPastDays
          disableWeekends
        />
      </div>
      <CL.Button type="button" size="m" disabled={!selectedDate || isCheckingAvailability} onClick={checkAvailability}>
        {t.U1QE(checkAvailabilityMsg).toUpperCase()}
      </CL.Button>
      {isCheckingAvailability && (
        <p role="status" aria-live="polite">
          {t.VZZW('Checking installation availability...')}
        </p>
      )}
      {availabilityStatus === InstallationAvailabilityStatus.ERROR && (
        <div role="alert" aria-live="assertive">
          <CL.Notification
            hasCloseButton={false}
            text={installationAvailability?.error || t.JDTO(somethingWentWrongMsg)}
            type="error"
            visible
          />
        </div>
      )}
      {availabilityStatus === InstallationAvailabilityStatus.SUCCESS && hasSelectedDate && (
        <p role="status" aria-live="polite">
          {t.VQTF('The selected date is available for installation')}
        </p>
      )}
      {availabilityStatus === InstallationAvailabilityStatus.EMPTY && (
        <p role="status" aria-live="polite">
          {t.JHZY('No installation dates are available for the selected date')}
        </p>
      )}
      {hasUnavailableSelectedDate && (
        <p className="of-installation-date-selection__error" role="alert">
          {t.KVWE('Selected date is not available for installation')}
        </p>
      )}
      {availableDates.length > 0 && (
        <fieldset className="of-installation-date-selection__available-dates">
          <legend>{t.VLYG('Please select from available dates or select a preferred installation date')}</legend>
          {availableDates.map(date => (
            <label className="of-installation-date-selection__date-option" key={date}>
              <input
                type="radio"
                name="available-installation-date"
                value={date}
                checked={normalizedSelectedDate === date}
                onChange={() => selectDate(date)}
              />
              <span>{date}</span>
            </label>
          ))}
          <CL.Button type="button" size="m" disabled={!canConfirmDate || isConfirming} onClick={confirmDate}>
            {t.X5NK('Confirm date').toUpperCase()}
          </CL.Button>
          {confirmedDate === normalizedSelectedDate && (
            <p role="status" aria-live="polite">
              {t.EQ10('The installation date has been confirmed')}
            </p>
          )}
        </fieldset>
      )}
    </section>
  );
};
